/** @format */

import BarChartIcon from "@mui/icons-material/BarChart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PieChartIcon from "@mui/icons-material/PieChart";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { useAppSelector } from "../../../../app/hooks";
import { selectAdmin } from "../../adminSlice";
import { DasboardDto } from "../../models";
import { useEffect, useMemo, useState, type ReactNode } from "react";

interface DisplayChartsProps {
  selectedYear: number;
  variant?: "admin" | "public";
  publicData?: DasboardDto | null;
  publicLoading?: boolean;
}

export default function DisplayCharts({
  selectedYear,
  variant = "admin",
  publicData,
  publicLoading = false,
}: DisplayChartsProps) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const adminAction = useAppSelector(selectAdmin);
  const [data, setData] = useState<DasboardDto>();
  const [raceData, setRaceData] = useState<number[]>([]);
  const [raceXData, setXRaceData] = useState<string[]>([]);
  const [univerityData, setUniverityData] = useState<number[]>([]);
  const [univerityXData, setXUniverityData] = useState<string[]>([]);

  useEffect(() => {
    if (variant === "public") {
      if (publicData) {
        setData(publicData);
        renderRaceData(publicData);
        renderUniversityData(publicData);
      } else {
        setData(undefined);
        setRaceData([]);
        setXRaceData([]);
        setUniverityData([]);
        setXUniverityData([]);
      }
      return;
    }
    if (adminAction.dashboardState.dashboard) {
      setData(adminAction.dashboardState.dashboard);
      renderRaceData(adminAction.dashboardState.dashboard);
      renderUniversityData(adminAction.dashboardState.dashboard);
    }
  }, [variant, publicData, adminAction.dashboardState.dashboard]);

  const chartReady =
    variant === "public"
      ? !publicLoading && Boolean(publicData)
      : Boolean(adminAction.dashboardState.dashboard);

  const renderRaceData = (records: DasboardDto) => {
    if (records) {
      const xdata: string[] = [];
      const rData: number[] = [];

      records.race.forEach((s) => {
        xdata.push(s.name);
        rData.push(s.value);
      });

      setRaceData(rData);
      setXRaceData(xdata);
    }
  };

  const renderUniversityData = (records: DasboardDto) => {
    if (records) {
      const xdata: string[] = [];
      const uData: number[] = [];

      records.universities.forEach((s) => {
        xdata.push(s.name);
        uData.push(s.value);
      });

      setUniverityData(uData);
      setXUniverityData(xdata);
    }
  };

  const chartColors = useMemo(
    () => ({
      race: theme.palette.success.main,
      genderMale: theme.palette.primary.main,
      genderFemale: "#c2185b",
      universities: theme.palette.warning.main,
    }),
    [theme]
  );

  const genderPieSize = isSm ? 260 : 280;
  const raceChartWidth = isSm ? 340 : 380;
  const uniChartWidth = isSm ? 520 : 900;

  const cardShell = {
    elevation: 0 as const,
    sx: {
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
      transition: "box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
      },
    },
  };

  function ChartCardHeader({
    icon,
    title,
    subtitle,
  }: {
    icon: ReactNode;
    title: string;
    subtitle: string;
  }) {
    return (
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: "primary.main",
              display: "flex",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="More options (coming soon)">
          <span>
            <IconButton size="small" className="no-print" aria-label="Chart options">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 4 }}>
      <Grid container spacing={2.5}>
        {data &&
          chartReady &&
          data.race &&
          data.race.length > 0 &&
          raceData.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card {...cardShell} sx={{ ...cardShell.sx, height: "100%" }}>
                <CardContent>
                  <ChartCardHeader
                    icon={<BarChartIcon fontSize="small" />}
                    title="Race distribution"
                    subtitle="Applicants by recorded race group"
                  />
                  <Box sx={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
                    <BarChart
                      xAxis={[
                        {
                          id: "race-categories",
                          data: raceXData,
                          scaleType: "band",
                        },
                      ]}
                      series={[
                        {
                          data: raceData,
                          color: chartColors.race,
                          label: "Applicants",
                        },
                      ]}
                      height={280}
                      width={isSm ? Math.min(raceChartWidth, 340) : 420}
                      margin={{ left: 48, right: 12, top: 12, bottom: 36 }}
                      slotProps={{
                        bar: { clipPath: "inset(0px round 6px 6px 0px 0px)" },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

        {data &&
          chartReady &&
          (data.maleApplicants > 0 || data.femaleApplicants > 0) && (
            <Grid item xs={12} md={6}>
              <Card {...cardShell} sx={{ ...cardShell.sx, height: "100%" }}>
                <CardContent>
                  <ChartCardHeader
                    icon={<PieChartIcon fontSize="small" />}
                    title="Gender split"
                    subtitle="Share of applicants with recorded gender"
                  />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <PieChart
                      skipAnimation
                      series={[
                        {
                          data: [
                            {
                              id: "male",
                              value: data.maleApplicants,
                              label: "Male",
                              color: chartColors.genderMale,
                            },
                            {
                              id: "female",
                              value: data.femaleApplicants,
                              label: "Female",
                              color: chartColors.genderFemale,
                            },
                          ],
                          innerRadius: 44,
                          outerRadius: 100,
                          paddingAngle: 1.5,
                          cornerRadius: 4,
                        },
                      ]}
                      width={genderPieSize}
                      height={genderPieSize}
                      slotProps={{
                        legend: {
                          direction: "row",
                          position: {
                            vertical: "bottom",
                            horizontal: "middle",
                          },
                          padding: 0,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

        {data &&
          chartReady &&
          data.universities &&
          data.universities.length > 0 &&
          univerityData.length > 0 && (
            <Grid item xs={12}>
              <Card {...cardShell} sx={{ ...cardShell.sx, overflowX: "auto" }}>
                <CardContent>
                  <ChartCardHeader
                    icon={<SchoolOutlinedIcon fontSize="small" />}
                    title="First-choice institutions"
                    subtitle="Volume by abbreviation — scroll horizontally on small screens"
                  />
                  <Box sx={{ minWidth: uniChartWidth }}>
                    <BarChart
                      xAxis={[
                        {
                          id: "uni-categories",
                          data: univerityXData,
                          scaleType: "band",
                        },
                      ]}
                      series={[
                        {
                          data: univerityData,
                          color: chartColors.universities,
                          label: "Applicants",
                        },
                      ]}
                      height={300}
                      width={uniChartWidth}
                      margin={{ left: 52, right: 16, top: 12, bottom: 52 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
      </Grid>
    </Box>
  );
}
